import React, { createContext, useContext, useState, useEffect } from "react";
import { validateDiscount } from "../Api/apiService";
import {
  CART_KEY,
  DISCOUNT_KEY,
  getStoredJson,
  setStoredJson,
} from "../Utils/storage";
import { getProductImage, parseImageList } from "../Utils/imageUtils";
import {
  formatOptionValues,
  getFiniteNumber,
  getOriginalPrice,
  getServerEffectivePrice,
  isTruthyFlag,
  parseOptionValues,
} from "../Utils/productUtils";

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const lineKeyOf = (item) =>
  `${String(item?.id || "")}:${
    (item?.variantId ?? item?.variant_id) !== null &&
    (item?.variantId ?? item?.variant_id) !== undefined &&
    String(item?.variantId ?? item?.variant_id) !== ""
      ? String(item?.variantId ?? item?.variant_id)
      : ""
  }`;

const sanitizeText = (value) => String(value || "").replace(/[<>]/g, "");

const getStoredVariantId = (item) => {
  const value = item.variantId ?? item.variant_id;
  if (value === null || value === undefined || value === "") return null;
  const parsed = getFiniteNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
};

const sanitizeStoredCartItem = (item) => {
  if (!item || typeof item !== "object") return null;

  const optionValues = parseOptionValues(
    item.optionValues ?? item.option_values,
  );
  const images = parseImageList(item.images);
  const image = item.image ? sanitizeText(item.image) : images[0] || null;
  const stock = Math.max(0, getFiniteNumber(item.stock, 0));
  const originalPrice = getFiniteNumber(item.compare_price);
  const normalized = {
    id: sanitizeText(item.id).trim(),
    name: sanitizeText(item.name),
    price: Math.max(0, getServerEffectivePrice(item)),
    shipping_price: Math.max(0, getFiniteNumber(item.shipping_price, 0)),
    stock,
    compare_price:
      originalPrice !== null && originalPrice >= 0 ? originalPrice : null,
    image,
    images,
    slug: sanitizeText(item.slug),
    quantity: Math.max(1, Math.min(Number(item.quantity) || 1, stock)),
    on_sale: isTruthyFlag(item.on_sale) ? 1 : 0,
    variantId: getStoredVariantId(item),
    optionValues: Object.keys(optionValues).length > 0 ? optionValues : null,
    variantName: item.variantName
      ? sanitizeText(item.variantName)
      : formatOptionValues(optionValues),
  };
  return { ...normalized, lineKey: lineKeyOf(normalized) };
};

const isValidStoredCartItem = (item) =>
  item && item.id && item.name && item.price > 0 && item.quantity > 0;

const sanitizeStoredCart = () => {
  const saved = getStoredJson(CART_KEY);
  return Array.isArray(saved)
    ? saved.map(sanitizeStoredCartItem).filter(isValidStoredCartItem)
    : [];
};

const getProductSaleState = (product, price, originalPrice) => {
  if (originalPrice === null || originalPrice <= price) return false;
  if (
    product.effective_price !== undefined ||
    product.display_on_sale !== undefined
  ) {
    return (
      isTruthyFlag(product.display_on_sale) &&
      getFiniteNumber(product.display_sale_price) !== null
    );
  }
  return (
    (isTruthyFlag(product.on_sale) &&
      getFiniteNumber(product.sale_price) !== null) ||
    getFiniteNumber(product.compare_price) > price
  );
};

const sanitizeProduct = (product) => {
  const price = getServerEffectivePrice(product);
  const originalPrice = getOriginalPrice(product, price);
  const productImages = parseImageList(product.images);
  const productImage = product.image
    ? sanitizeText(product.image)
    : productImages[0] || getProductImage(product);
  const hasSale = getProductSaleState(product, price, originalPrice);

  return {
    id: sanitizeText(product.id).trim(),
    name: sanitizeText(product.name),
    price,
    shipping_price: getFiniteNumber(product.shipping_price, 0),
    stock: Math.max(0, getFiniteNumber(product.stock, 0)),
    compare_price: hasSale ? originalPrice : null,
    image: productImage,
    images: productImages.length > 0 ? productImages : [productImage],
    slug: sanitizeText(product.slug),
    on_sale: hasSale ? 1 : 0,
  };
};

const sanitizeVariant = (variant, product) => {
  if (!variant || !variant.id) return null;

  const variantImages = parseImageList(variant.images);
  const optionValues = parseOptionValues(variant.option_values);
  const variantPrice = getServerEffectivePrice(variant);
  const variantOriginalPrice = isTruthyFlag(variant.on_sale)
    ? getOriginalPrice(variant, variantPrice)
    : null;

  return {
    variantId: getFiniteNumber(variant.id),
    price: variantPrice,
    compare_price: variantOriginalPrice,
    stock: Math.max(0, getFiniteNumber(variant.stock, 0)),
    shipping_price:
      variant.shipping_price !== null && variant.shipping_price !== undefined
        ? getFiniteNumber(variant.shipping_price, 0)
        : product.shipping_price,
    on_sale: variantOriginalPrice !== null ? 1 : 0,
    optionValues: Object.keys(optionValues).length > 0 ? optionValues : null,
    variantName: formatOptionValues(optionValues),
    images: variantImages.length > 0 ? variantImages : product.images,
    image: variantImages[0] || product.image,
  };
};

const getCartValidationError = (product, variant) => {
  if (!product || !product.id) return "Invalid product data";

  const productVariants = Array.isArray(product.variants)
    ? product.variants
    : [];
  const hasConfiguredVariants =
    isTruthyFlag(product.has_variants) ||
    isTruthyFlag(product.has_configured_variants) ||
    productVariants.length > 0;
  if (hasConfiguredVariants && (!variant || !variant.id)) {
    return "A configured product requires a selected variant";
  }
  if (
    variant?.product_id !== undefined &&
    String(variant.product_id) !== String(product.id)
  ) {
    return "Variant does not belong to product";
  }
  if (
    variant &&
    variant.is_active !== undefined &&
    !isTruthyFlag(variant.is_active)
  ) {
    return "Cannot add an inactive variant";
  }
  return null;
};

const isValidVariantId = (variantId) =>
  variantId === null || (Number.isInteger(variantId) && variantId > 0);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(sanitizeStoredCart);

  const [discount, setDiscount] = useState(() => {
    const saved = getStoredJson(DISCOUNT_KEY);
    return saved || null;
  });

  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    setStoredJson(CART_KEY, cart);
  }, [cart]);

  useEffect(() => {
    setStoredJson(DISCOUNT_KEY, discount);
  }, [discount]);

  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isCartOpen]);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen((prev) => !prev);

  /**
   * addToCart(product, quantity, variant?)
   *
   * With a variant (selected on the product page), the effective price,
   * original price, stock, shipping, image and sale flag all come from the
   * server-computed variant payload. Non-variant products behave as before.
   */
  const addToCart = (product, quantity = 1, variant = null) => {
    const validationError = getCartValidationError(product, variant);
    if (validationError) {
      console.error(validationError);
      return;
    }

    const variantId = variant ? getFiniteNumber(variant.id) : null;
    if (variant && !isValidVariantId(variantId)) {
      console.error("Invalid variant data");
      return;
    }
    const sanitizedProduct = sanitizeProduct(product);
    const sanitizedVariant = sanitizeVariant(variant, sanitizedProduct);

    const finalItem = sanitizedVariant
      ? {
          ...sanitizedProduct,
          ...sanitizedVariant,
          compare_price: sanitizedVariant.compare_price,
        }
      : sanitizedProduct;

    if (
      finalItem.price < 0 ||
      finalItem.stock <= 0 ||
      !Number.isFinite(finalItem.price)
    ) {
      console.error("Invalid product data");
      return;
    }

    const key = lineKeyOf(finalItem);

    setCart((prev) => {
      const existing = prev.find((item) => item.lineKey === key);
      const stockLimit = Math.max(0, finalItem.stock);
      const nextQuantity = Math.min(
        (existing ? existing.quantity : 0) + Math.max(1, quantity),
        stockLimit,
      );

      if (existing) {
        return prev.map((item) =>
          item.lineKey === key ? { ...item, quantity: nextQuantity } : item,
        );
      }

      const initialQuantity = Math.min(Math.max(1, quantity), stockLimit);
      return [
        ...prev,
        {
          ...finalItem,
          lineKey: key,
          quantity: initialQuantity,
        },
      ];
    });
    openCart();
  };

  const removeFromCart = (lineKey) => {
    setCart((prev) => prev.filter((item) => item.lineKey !== lineKey));
  };

  const updateQuantity = (lineKey, quantity) => {
    const parsedQuantity = Math.round(Number(quantity));
    if (!Number.isFinite(parsedQuantity) || parsedQuantity < 1) {
      removeFromCart(lineKey);
      return;
    }
    const sanitizedQuantity = parsedQuantity;

    setCart((prev) =>
      prev.map((item) => {
        if (item.lineKey === lineKey) {
          const stockLimit = Math.max(0, item.stock);
          const newQuantity = Math.min(sanitizedQuantity, stockLimit);
          return { ...item, quantity: newQuantity };
        }
        return item;
      }),
    );
  };

  const clearCart = () => {
    setCart([]);
    setDiscount(null);
    setDiscountError(null);
    closeCart();
  };

  const applyDiscount = async (code) => {
    if (!code || !code.trim()) {
      setDiscountError("Please enter a discount code");
      return false;
    }

    const hasSaleItems = cart.some((item) => item.on_sale === 1);

    if (hasSaleItems) {
      setDiscountError("Discount codes cannot be applied to sale items");
      return false;
    }

    setDiscountLoading(true);
    setDiscountError(null);

    try {
      const result = await validateDiscount(code.trim().toUpperCase());
      if (result.success) {
        const discountValue = Number(result.value);
        if (isNaN(discountValue)) {
          throw new Error("Invalid discount value");
        }
        setDiscount({
          code: result.code,
          type: result.type === "percent" ? "percentage" : result.type,
          value: discountValue,
          expires_at: result.expires_at,
          max_uses_per_user: result.max_uses_per_user || null,
        });
        setDiscountError(null);
        return true;
      } else {
        setDiscountError(result.message || "Invalid discount code");
        return false;
      }
    } catch (error) {
      setDiscountError(error.message || "Invalid discount code");
      return false;
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
    setDiscountError(null);
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const cartSubtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const discountAmount = (() => {
    if (!discount) return 0;

    const type = discount.type;
    const value = Number(discount.value);

    if (type === "percentage" || type === "percent") {
      return (cartSubtotal * value) / 100;
    }

    if (type === "fixed") {
      return Math.min(value, cartSubtotal);
    }

    console.warn("Unknown discount type:", type);
    return 0;
  })();

  const shippingCost = cart.reduce(
    (sum, item) => sum + (item.shipping_price || 0) * item.quantity,
    0,
  );

  const cartTotal = Math.max(0, cartSubtotal - discountAmount + shippingCost);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        cartSubtotal,
        cartTotal,
        shippingCost,
        discount,
        discountAmount,
        discountLoading,
        discountError,
        applyDiscount,
        removeDiscount,
        isCartOpen,
        openCart,
        closeCart,
        toggleCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
